import { Configuration, PopupRequest } from '@azure/msal-browser';
import { IdentityProviderResponse } from '@/types';
import { getApiBaseUrl } from './env';

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */

// Backend callback Azure AD redirects to: API base URL (already includes /api/v1) + /auth/callback.
// Must be an absolute URL, so VITE_API_BASE_URL can't be a proxied relative path.
const getBackendCallbackUrl = () => `${getApiBaseUrl()}/auth/callback`;

export function buildMsalConfig(identity: IdentityProviderResponse): Configuration {
  const resolvedAuthority = identity.authority?.startsWith('http')
    ? identity.authority
    : `https://login.microsoftonline.com/${identity.tenantId}`;

  const backendCallbackUrl = getBackendCallbackUrl();

  return {
    auth: {
      clientId: identity.clientId.trim(),
      authority: resolvedAuthority,
      redirectUri: backendCallbackUrl, // Point to backend callback
      postLogoutRedirectUri: window.location.origin,
      navigateToLoginRequestUrl: false, // Backend handles redirect
    },
    cache: {
      cacheLocation: 'sessionStorage', // Use sessionStorage for server-side flow
      storeAuthStateInCookie: false,
    },  
    system: {
      allowNativeBroker: false,
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) {
            return;
          }
          switch (level) {
            case 0:
              console.error(message);
              return;
            case 1:
              console.warn(message);
              return;
            case 2:
              console.info(message);
              return;
            case 3:
              console.debug(message);
              return;
          }
        },
      },
    },
  };
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest: PopupRequest = {
  scopes: ['User.Read', 'profile', 'email', 'openid'],
};

/**
 * Build authorization URL for direct backend flow
 * This bypasses MSAL and redirects directly to Azure AD
 */
export function buildAuthorizationUrl(identity: IdentityProviderResponse, subdomain: string): string {
  const backendCallbackUrl = getBackendCallbackUrl();
  const authority = identity.authority?.startsWith('http')
    ? identity.authority
    : `https://login.microsoftonline.com/${identity.tenantId}`;

  // Encode tenant info and the SPA origin; the backend validates the origin against its allow-list.
  const clientId = identity.clientId.trim();
  const state = btoa(JSON.stringify({
    tenantId: identity.tenantId,
    clientId: clientId,
    subdomain: subdomain,
    origin: window.location.origin
  }));

  const authUrl = new URL(`${authority}/oauth2/v2.0/authorize`);
  authUrl.searchParams.append('client_id', clientId);
  authUrl.searchParams.append('response_type', 'code');
  authUrl.searchParams.append('redirect_uri', backendCallbackUrl);
  authUrl.searchParams.append('response_mode', 'query');
  authUrl.searchParams.append('scope', 'User.Read profile email openid offline_access');
  authUrl.searchParams.append('state', state);
  authUrl.searchParams.append('prompt', 'select_account');

  return authUrl.toString();
}

/**
 * Add here the endpoints and scopes for the web API you would like to use.
 */
export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
  graphPhotoEndpoint: 'https://graph.microsoft.com/v1.0/me/photo/$value',
};
